import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in
  const { syncFromUser } = useTheme();

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      syncFromUser(data.theme);
    } catch (e) {
      setUser(false);
    }
  }, [syncFromUser]);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  useEffect(() => {
    const onExpired = () => setUser(false);
    window.addEventListener("gx-session-expired", onExpired);
    return () => window.removeEventListener("gx-session-expired", onExpired);
  }, []);

  const login = useCallback(
    async (email, password) => {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      syncFromUser(data.theme);
      return data;
    },
    [syncFromUser]
  );

  const register = useCallback(async (payload) => {
    // Email verification required — do NOT auto-login. Returns { needs_verification, email }.
    const { data } = await api.post("/auth/register", payload);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // ignore
    }
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loadMe, formatApiErrorDetail }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
